import { useEffect } from "react";
import { Link } from "react-router-dom";
import EndpointExample from "./EndpointExample.js";
import ApiTitle from "./ApiTitle.js";

function Api() {
  useEffect(() => {
    document.title = "TARO - OAI-PMH API";
  }, []);

  // --- Core examples (standard OAI-PMH verbs) ---
  const identify = {
    parameter: "Identify",
    description:
      "Returns repository-level information about this OAI-PMH endpoint (useful for confirming you are hitting the correct service).",
    link: "https://www.txarchives.org/oai/?verb=Identify",
    returns: "an <Identify> response with repository and protocol details (XML).",
  };

  const listSets = {
    parameter: "ListSets",
    description:
      "Returns all available sets. In TARO, set values map 1:1 with the repository slug used across the site UI and Search API.",
    link: "https://www.txarchives.org/oai/?verb=ListSets",
    returns:
      "an XML list of <set> entries; use a setSpec value (e.g., utlac) in ListIdentifiers/ListRecords to harvest by repository.",
  };

  const listMetadataFormats = {
    parameter: "ListMetadataFormats",
    description:
      "Returns the metadata formats supported by this endpoint (the most common is oai_dc).",
    link: "https://www.txarchives.org/oai/?verb=ListMetadataFormats",
    returns:
      "an XML list of available metadataPrefix values (e.g., oai_dc) and their schemas/namespaces.",
  };

  const listIdentifiers = {
    parameter: "ListIdentifiers",
    description:
      'Returns record headers (identifiers + datestamps). Use "set" to scope to a TARO repository, and "metadataPrefix" to choose the metadata format.',
    link: "https://www.txarchives.org/oai/?verb=ListIdentifiers&metadataPrefix=oai_dc&set=utlac",
    returns:
      'a list of record headers with <identifier> values you can feed into GetRecord. (May include a resumptionToken for paging.)',
  };

  const getRecord = {
    parameter: "GetRecord",
    description:
      'Returns one record (header + metadata). The identifier must be a TARO finding aid identifier (urn:taro:...).',
    link: "https://www.txarchives.org/oai/?verb=GetRecord&metadataPrefix=oai_dc&identifier=urn:taro:utexas.cah.01015",
    returns:
      "the full OAI-PMH record for that identifier in the selected metadata format (XML).",
  };

  const listRecords = {
    parameter: "ListRecords",
    description:
      "Returns full records (not just headers). This is the typical harvesting verb if you want metadata content in bulk.",
    link: "https://www.txarchives.org/oai/?verb=ListRecords&metadataPrefix=oai_dc&set=utlac",
    returns:
      "a stream of full records (header + metadata). Large result sets may be paged with a resumptionToken.",
  };

  // --- Practical / operational examples ---
  const incrementalHarvest = {
    parameter: "Incremental harvesting (from/until)",
    description:
      'Use "from" and/or "until" to harvest changes within a date (or datetime) range. The safest approach is to use the same datestamp granularity/format that you observe in OAI-PMH record headers returned by ListIdentifiers/ListRecords.',
    link: "https://www.txarchives.org/oai/?verb=ListRecords&metadataPrefix=oai_dc&set=utlac&from=YYYY-MM-DD",
    returns:
      "records for the given set that have datestamps on/after the provided from value (format depends on the datestamp granularity supported by the server).",
  };

  const pagingNote = {
    parameter: "Paging (resumptionToken)",
    description:
      'For large harvests, responses may include a resumptionToken. When present, continue the harvest by calling the same verb again using ONLY "resumptionToken" (do not include verb-specific filters like metadataPrefix, set, from, or until). The token encapsulates the original request.',
    link: "https://www.txarchives.org/oai/?verb=ListRecords&resumptionToken=REPLACE_WITH_TOKEN_FROM_RESPONSE",
    returns:
      "the next page of results. resumptionToken values are generated by the server and must be copied from the previous response.",
  };

  // --- TARO-specific notes / guidance ---
  const identifierNote = {
    parameter: "TARO record identifiers (identifier=...)",
    description:
      "OAI-PMH record identifiers in TARO are sourced from the finding aid’s EAD identifier (eadid) and look like urn:taro:<...>. These values are authoritative, but they are not guaranteed to match URL path segments for the raw XML. Do not attempt to derive identifiers from /admin/* URLs. Best practice is to discover identifiers via ListIdentifiers (optionally scoped by set) and then use those identifiers with GetRecord.",
    link: "https://www.txarchives.org/oai/?verb=ListIdentifiers&metadataPrefix=oai_dc&set=utlac",
    returns:
      "a list of valid <identifier> values for that repository (set); use any returned identifier value in GetRecord.",
  };

  const deletedRecordsNote = {
    parameter: "Deleted records",
    description:
      'Some OAI-PMH repositories expose deletions using <header status="deleted">. If you encounter deleted headers during harvesting, treat them as tombstones (the record metadata may be absent).',
    link: "https://www.txarchives.org/oai/?verb=ListIdentifiers&metadataPrefix=oai_dc&set=utlac",
    returns:
      "if deletions are exposed, some headers may include status=\"deleted\" indicating the record should be considered removed for harvesting purposes.",
  };

  return (
    <div>
      <div className="blue-grey">
        <div className="container">
          <ApiTitle />
          <hr />
          <div className="container">
            <div className="card-body pb-4 pt-1">
              <center>
                <h3>Using the API</h3>
                <p>
                  <em>
                    Looking for the JSON Search API that powers this site? See{" "}
                    <Link to={`/search-api`}>Search API</Link> and{" "}
                    <Link to={`/search-api/params`}>Search API Parameters</Link>.
                  </em>
                </p>
              </center>

              <br />

              <h5 className="card-title">Base Endpoint</h5>
              <p className="card-text">
                The base endpoint is{" "}
                <strong>https://www.txarchives.org/oai/</strong>. OAI-PMH
                requests are made by providing a <strong>verb</strong> and any
                required parameters (such as <strong>metadataPrefix</strong>).
                Results are returned in <strong>XML</strong>.
              </p>

              <h5 className="card-title mt-4">Minimum Required Parameters</h5>
              <p className="card-text mb-2">
                The following are the minimum required parameters for common
                OAI-PMH verbs:
              </p>
              <ul className="card-text">
                <li>
                  <strong>Identify</strong>: <code>verb</code>
                </li>
                <li>
                  <strong>ListSets</strong>: <code>verb</code>
                </li>
                <li>
                  <strong>ListMetadataFormats</strong>: <code>verb</code>{" "}
                  (optionally <code>identifier</code>)
                </li>
                <li>
                  <strong>ListIdentifiers</strong>: <code>verb</code> +{" "}
                  <code>metadataPrefix</code> (optional <code>set</code>,{" "}
                  <code>from</code>, <code>until</code>)
                </li>
                <li>
                  <strong>ListRecords</strong>: <code>verb</code> +{" "}
                  <code>metadataPrefix</code> (optional <code>set</code>,{" "}
                  <code>from</code>, <code>until</code>)
                </li>
                <li>
                  <strong>GetRecord</strong>: <code>verb</code> +{" "}
                  <code>metadataPrefix</code> + <code>identifier</code>
                </li>
              </ul>

              {EndpointExample(identify)}
            </div>

            <hr />

            <div className="card-body pb-4 pt-1">
              <h5 className="card-title">Discovering Repositories (Sets)</h5>
              <p className="card-text">
                TARO uses OAI-PMH <strong>sets</strong> to support harvesting by
                repository. The <strong>set</strong> value maps 1:1 with the
                repository slug used across the TARO site (for example,{" "}
                <strong>utlac</strong>).
              </p>

              {EndpointExample(listSets)}
            </div>

            <hr />

            <div className="card-body pb-4 pt-1">
              <h5 className="card-title">Discovering Supported Formats</h5>
              <p className="card-text">
                Use <strong>ListMetadataFormats</strong> to see which
                metadataPrefix values are supported (for example,{" "}
                <strong>oai_dc</strong>).
              </p>

              {EndpointExample(listMetadataFormats)}
            </div>

            <hr />

            <div className="card-body pb-4 pt-1">
              <h5 className="card-title">Harvesting by Repository</h5>
              <p className="card-text">
                A typical workflow is:
              </p>
              <ol className="card-text mt-2">
                <li>
                  Call <strong>ListIdentifiers</strong> with{" "}
                  <strong>set</strong> and <strong>metadataPrefix</strong> to
                  discover record identifiers.
                </li>
                <li>
                  Use those <strong>identifier</strong> values with{" "}
                  <strong>GetRecord</strong>, or use <strong>ListRecords</strong>{" "}
                  to harvest full records in bulk.
                </li>
                <li>
                  For large harvests, continue using <strong>resumptionToken</strong>{" "}
                  if provided.
                </li>
              </ol>

              {EndpointExample(listIdentifiers)}
              {EndpointExample(getRecord)}
              {EndpointExample(listRecords)}
            </div>

            <hr />

            <div className="card-body pb-4 pt-1">
              <h5 className="card-title">Incremental Harvesting</h5>
              <p className="card-text">
                Many harvesters run incrementally using <strong>from</strong> and{" "}
                <strong>until</strong>. Use the datestamp format you observe in
                OAI-PMH responses. If you are unsure about supported granularity,
                start by inspecting headers returned by{" "}
                <strong>ListIdentifiers</strong>.
              </p>

              {EndpointExample(incrementalHarvest)}
            </div>

            <hr />

            <div className="card-body pb-4 pt-1">
              <h5 className="card-title">Paging</h5>
              <p className="card-text">
                Large result sets may be split across multiple responses using
                a <strong>resumptionToken</strong>. The safest rule: when a
                resumptionToken is present, your next request should include
                <strong> only</strong> the verb and the resumptionToken (do not
                include metadataPrefix, set, from, or until).
              </p>

              {EndpointExample(pagingNote)}
            </div>

            <hr />

            <div className="card-body pb-4 pt-1">
              <h5 className="card-title">TARO-Specific Notes</h5>
              {EndpointExample(identifierNote)}
              {EndpointExample(deletedRecordsNote)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Api;
